import { callingObject as receiverObjects, computedFunctions } from "./schema";
export const renderLiquid = (rule, platform)=>{
    if (rule.conditions.some((condition)=>condition.type === "group")) {
        if (platform === "IOS") {
            return 'device.sdkVersionPadded >= "003.011.000"';
        } else if (platform === "ANDROID") {
            return 'device.sdkVersionPadded >= "001.050.000"';
        }
        return 'device.sdkVersionPadded > "003.011.000"';
    }
    const output = rule.conditions// filter out groups
    .filter((condition)=>condition.type !== "group").map((condition)=>renderCondition(condition)).join(" " + rule.operator + " ");
    return output;
};
export const operatorsWithoutRHS = [
    "t",
    "f",
    "null",
    "nnull"
];
const renderCondition = (condition)=>{
    return `${renderLHS(condition.lhs)} ${renderOperator(condition.operator)} ${operatorsWithoutRHS.includes(condition.operator) ? "" : renderRHS(condition.rhs)}`.trim();
};
const renderOperator = (operator)=>{
    switch(operator){
        case "t":
            return "== true";
        case "f":
            return "== false";
        case "eq":
            return "==";
        case "neq":
            return "!=";
        case "gt":
            return ">";
        case "gte":
            return ">=";
        case "lt":
            return "<";
        case "lte":
            return "<=";
        case "c":
            return "contains";
        case "null":
            return "== null";
        case "nnull":
            return "!= null";
    }
};
const renderLHS = (lhs)=>{
    return lhs.value;
};
const renderRHS = (rhs)=>{
    switch(rhs.type){
        case "number":
            return rhs.value.toString();
        case "boolean":
            return rhs.value.toString();
        case "string":
            return `"${rhs.value}"`;
        case "computed_function":
            return rhs.value;
        case "property":
            return rhs.value;
    }
};
const mapOperatorToCEL = (operator)=>{
    switch(operator){
        case "and":
            return "&&";
        case "or":
            return "||";
        default:
            return operator;
    }
};
const renderCELVersionCondition = (rule)=>{
    const hasGroups = rule?.conditions.some((condition)=>condition.type === "group");
    if (hasGroups) {
        return `(device.platform == "iOS" && device.sdkVersionPadded >= "003.011.000") || (device.platform == "Android" && device.sdkVersionPadded >= "001.050.000")`;
    }
    return undefined;
};
export const _renderCEL = (rule, options)=>{
    const output = rule ? rule.conditions.map((condition)=>renderCELCondition(condition, options)).join(" " + mapOperatorToCEL(rule.operator) + " ") : undefined;
    const entitlements = rule?.entitlements || {
        rule: {
            type: "no_active_entitlements"
        },
        type: "entitlement"
    };
    const entitlement = renderCELEntitlementCondition(entitlements.rule);
    const version = renderCELVersionCondition(rule);
    if (output) {
        const parts = [];
        if (entitlement || version) {
            if (entitlement) parts.push(`(${entitlement})`);
            if (version) parts.push(`(${version})`);
            parts.push(`(${output})`);
            return parts.join(" && ");
        }
        return output;
    } else {
        return entitlement;
    }
};
export const renderCEL = (rule, options)=>{
    const output = _renderCEL(rule, options);
    return output === "" ? null : output;
};
// Renders Condition values as CEL expressions, with properties being converted to computed functions where necessary
const renderCELValue = (value)=>{
    switch(value.type){
        case "computed_function":
            return value.value;
        case "property":
            {
                let parsed = createCELFunctionFromProperty(value.value);
                if (parsed) {
                    return parsed.value;
                } else {
                    return value.value;
                }
            }
        case "number":
            return value.value.toString();
        case "boolean":
            return value.value.toString();
        case "string":
            return `"${value.value}"`;
    }
};
const renderCELCondition = (condition, options)=>{
    if (condition.type === "group") {
        const groupConditions = condition.conditions.map((c)=>renderCELCondition(c, options)).join(" " + mapOperatorToCEL(condition.operator) + " ");
        return `(${groupConditions})`;
    }
    const left = renderCELValue(condition.lhs);
    const right = ()=>renderCELValue(condition.rhs);
    let expression = "";
    switch(condition.operator){
        case "t":
            expression = `${left} == true`;
            break;
        case "f":
            expression = `${left} == false`;
            break;
        case "eq":
            expression = `${left} == ${right()}`;
            break;
        case "neq":
            expression = `${left} != ${right()}`;
            break;
        case "gt":
            expression = `${left} > ${right()}`;
            break;
        case "gte":
            expression = `${left} >= ${right()}`;
            break;
        case "lt":
            expression = `${left} < ${right()}`;
            break;
        case "lte":
            expression = `${left} <= ${right()}`;
            break;
        case "c":
            expression = `${left}.contains(${right()})`;
            break;
        case "null":
            expression = `${left} == null`;
            break;
        case "nnull":
            expression = `${left} != null`;
            break;
    }
    if (options?.ensureKeyExists) {
        return `(has(${condition.lhs.value}) && ${expression})`;
    }
    return expression;
};
const renderCELEntitlementCondition = (condition)=>{
    const activeIdentifiers = condition.type === "entitlement_conditions" ? condition.conditions.filter((x)=>x.status === "active").map((x)=>x.identifier) : [];
    const inactiveIdentifiers = condition.type === "entitlement_conditions" ? condition.conditions.filter((x)=>x.status === "inactive").map((x)=>x.identifier) : [];
    const willNotRenewIdentifiers = condition.type === "entitlement_conditions" ? condition.conditions.filter((x)=>x.status === "will_not_renew") : [];
    const inTrialIdentifiers = condition.type === "entitlement_conditions" ? condition.conditions.filter((x)=>x.status === "in_trial") : [];
    const expiredIdentifiers = condition.type === "entitlement_conditions" ? condition.conditions.filter((x)=>x.status === "expired") : [];
    const containsActive = activeIdentifiers.length > 0 ? `${JSON.stringify(activeIdentifiers)}.all(identifier, device.activeEntitlements.contains(identifier))` : "";
    const containsInactive = inactiveIdentifiers.length > 0 ? `${JSON.stringify(inactiveIdentifiers)}.all(identifier, !device.activeEntitlements.contains(identifier))` : "";
    const containsWillNotRenew = willNotRenewIdentifiers.map((x)=>`device.customerInfo.entitlements.exists(e, e.id == "${x.identifier}" && e.isActive == true && e.willRenew == false)`);
    const containsInTrial = inTrialIdentifiers.map((x)=>`device.customerInfo.entitlements.exists(e, e.id == "${x.identifier}" && e.isActive == true && e.willRenew == false)`);
    const containsExpired = expiredIdentifiers.map((x)=>`device.customerInfo.entitlements.exists(e, e.id == "${x.identifier}" && e.state == "expired")`);
    switch(condition.type){
        case "no_active_entitlements":
            return "size(device.activeEntitlements) == 0";
        case "entitlement_conditions":
            const nonCustomerInfoParts = [
                containsActive,
                containsInactive
            ].filter((x)=>x);
            const customerInfoParts = [
                ...containsWillNotRenew,
                ...containsInTrial,
                ...containsExpired
            ].filter((x)=>x);
            const parts = [
                ...nonCustomerInfoParts,
                ...customerInfoParts.length > 0 ? [
                    `has(device.customerInfo) && (${customerInfoParts.join(" && ")})`
                ] : []
            ].filter((x)=>x);
            return parts.join(" && ");
        case "always_true":
            return "";
        case "any_will_not_renew":
            return "has(device.customerInfo) && device.customerInfo.entitlements.exists(e, e.isActive == true && e.willRenew == false)";
        case "any_in_trial":
            return 'has(device.customerInfo) && device.customerInfo.entitlements.exists(e, e.isActive == true && e.willRenew == false && e.offerType == "trial")';
        case "any_active_subs_auto_renew_disabled":
            return 'has(device.customerInfo) && device.customerInfo.entitlements.exists(e, e.isActive == true && e.willRenew == false && e.offerType != "trial")';
        case "any_expired_no_active":
            return 'has(device.customerInfo) && device.customerInfo.entitlements.exists(e, e.state == "expired") && device.customerInfo.entitlements.all(e, e.isActive == false)';
    }
};
export const createCELFunctionFromProperty = (property)=>{
    if (property.indexOf(".") === -1 || property.indexOf("_") === -1) return undefined;
    const parts = property.split(".");
    // If it starts with one of the defined groups, then it is a computed property function
    let group = parts.shift();
    let receiver = receiverObjects.safeParse(group);
    if (receiver.success) {
        // The receiver object of the function
        let remaining = parts[0].split("_");
        // Check if it is one of the predefined computed functions
        let functionName = remaining.shift();
        let parsedFunction = computedFunctions.safeParse(functionName);
        if (parsedFunction.success) {
            //It is a computed property function, we take the name and rejoin the rest as arguments
            let args = remaining.join("_");
            return {
                type: "computed_function",
                caller: receiver.data,
                name: parsedFunction.data,
                args: args,
                value: `${receiver.data}.${parsedFunction.data}("${args}")`
            };
        } else {
            // It is a property - return undefined
            return undefined;
        }
    } else {
        // Since it has no receiver object, it is definitely just a property - return undefined
        return undefined;
    }
};
